import { QueryObserverResult, useMutation, UseMutationResult, useQuery, useQueryClient } from 'react-query';
import { createNewsNew, deleteNewsNew, getNewsNew, getNewsNewList, updateNewsNew, uploadFile } from 'api/news-new';
import { AxiosError } from 'axios';
import { BaseQueryParams } from 'common/models';
import { Paginated } from 'common/utils';

import { DEFAULT_PAGINATED_LIST_ITEMS_PARAMS } from '../common/constants/common';
import { PublicFile } from '../common/models/file';
import { HttpException } from '../common/models/httpException';
import { CreateNewsNewRequest } from '../modules/NewsNew/models/create-news-new-request';
import { NewsNewResponse } from '../modules/NewsNew/models/news-new-response';
import { UpdateNewsNewRequest } from '../modules/NewsNew/models/update-news-new-request';

export const CK_NEWS_NEW = 'newsNew';

export const useNewsNewList = (
  params: BaseQueryParams = DEFAULT_PAGINATED_LIST_ITEMS_PARAMS,
): QueryObserverResult<Paginated<NewsNewResponse>, AxiosError<HttpException>> => {
  return useQuery(
    [CK_NEWS_NEW, { text: params.searchText, page: params.page, limit: params.limit }],
    () => getNewsNewList(params),
    {
      keepPreviousData: true,
    },
  );
};

export const useNewsNew = (id: string): QueryObserverResult<NewsNewResponse, AxiosError<HttpException>> => {
  return useQuery([CK_NEWS_NEW, id], () => getNewsNew(id));
};

export const useCreateNewsNew = (): UseMutationResult<
  NewsNewResponse,
  AxiosError<HttpException>,
  CreateNewsNewRequest
> => {
  const queryClient = useQueryClient();
  return useMutation(createNewsNew, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(CK_NEWS_NEW);
    },
  });
};

export const useUpdateNewsNew = (): UseMutationResult<
  NewsNewResponse,
  AxiosError<HttpException>,
  { id: string; request: UpdateNewsNewRequest }
> => {
  const queryClient = useQueryClient();
  return useMutation(({ id, request }: { id: string; request: UpdateNewsNewRequest }) => updateNewsNew(id, request), {
    onSuccess: async () => {
      await queryClient.invalidateQueries(CK_NEWS_NEW);
    },
  });
};

export const useDeleteNewsNew = (): UseMutationResult<void, AxiosError<HttpException>, string> => {
  const queryClient = useQueryClient();
  return useMutation((id: string) => deleteNewsNew(id), {
    onSuccess: async () => {
      await queryClient.invalidateQueries(CK_NEWS_NEW);
    },
  });
};

export const useUploadFile = (): UseMutationResult<PublicFile, AxiosError<HttpException>, { image: File }> => {
  return useMutation(({ image }: { image: File }) => uploadFile(image));
};
